import { getLocale, useTranslation } from '@rhino-project/core/i18n';
import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import {
  AlertDialog,
  Button,
  Card,
  Disclosure,
  Form,
  Typography
} from '@heroui/react';
import {
  FormErrors,
  ModelCreateSimple,
  ModelEditSimple,
  ModelFieldGroup
} from '@rhino-project/ui-heroui';
import { Icon } from '@iconify/react';
import {
  useModelCreateContext,
  useModelDelete,
  useModelEditContext,
  useModelIndex,
  useModelIndexInfinite
} from '@rhino-project/core/hooks';
import { useFormContext } from 'react-hook-form';
import { getLocalTimeZone, parseDate, today } from '@internationalized/date';
import type { RhinoRead } from '@rhino-project/core';

type PracticePlan = RhinoRead<'practice_plan'>;

const FIELD_PATHS = ['title', 'plan_date', 'description', 'pdf_attachment'];

const todayIso = () => today(getLocalTimeZone()).toString();

const PracticePlanThumb = ({ thumbUrl }: { thumbUrl?: string }) => {
  const [failed, setFailed] = useState(false);
  const showImage = thumbUrl && !failed;

  return (
    <div className="w-12 h-16 bg-accent rounded flex items-center justify-center shrink-0 overflow-hidden">
      {showImage ? (
        <img
          src={thumbUrl}
          alt=""
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <Icon icon="bi:file-pdf" className="text-xl text-accent-foreground" />
      )}
    </div>
  );
};

type FormMode =
  { kind: 'none' } | { kind: 'create' } | { kind: 'edit'; id: number };

const PracticePlanCreateForm = ({ onDone }: { onDone: () => void }) => {
  const { t } = useTranslation();

  const { mutate, isPending } = useModelCreateContext();
  const { handleSubmit } = useFormContext();

  const onSave = () =>
    handleSubmit((values) =>
      mutate(values as never, { onSuccess: () => onDone() })
    )();

  return (
    <Card className="p-6 mb-6">
      <Card.Header>
        <Card.Title>{t('frontend.messages.new_practice_plan')}</Card.Title>
      </Card.Header>
      <Form>
        <FormErrors />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <ModelFieldGroup path="title" variant="secondary" />
          <ModelFieldGroup path="plan_date" variant="secondary" />
        </div>
        <ModelFieldGroup path="description" variant="secondary" />
        <div className="mt-4">
          <ModelFieldGroup
            path="pdf_attachment"
            label={t('frontend.messages.practice_plan_pdf')}
            accept="application/pdf"
          />
        </div>
      </Form>
      <div className="flex gap-2 mt-4">
        <Button
          variant="primary"
          onPress={onSave}
          isDisabled={isPending}
          data-testid="practice-plan-save"
        >
          {t('frontend.messages.add')}
        </Button>
        <Button variant="ghost" onPress={onDone}>
          {t('frontend.messages.cancel')}
        </Button>
      </div>
    </Card>
  );
};

const PracticePlanEditForm = ({ onDone }: { onDone: () => void }) => {
  const { t } = useTranslation();

  const { mutate, isPending } = useModelEditContext();
  const { handleSubmit } = useFormContext();

  const onSave = () =>
    handleSubmit((values) =>
      mutate(values as never, { onSuccess: () => onDone() })
    )();

  return (
    <Card className="p-6 mb-6">
      <Card.Header>
        <Card.Title>{t('frontend.messages.edit_practice_plan')}</Card.Title>
      </Card.Header>
      <Form>
        <FormErrors />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <ModelFieldGroup path="title" variant="secondary" />
          <ModelFieldGroup path="plan_date" variant="secondary" />
        </div>
        <ModelFieldGroup path="description" variant="secondary" />
        <div className="mt-4">
          <ModelFieldGroup
            path="pdf_attachment"
            label={t('frontend.messages.practice_plan_pdf')}
            accept="application/pdf"
          />
        </div>
      </Form>
      <div className="flex gap-2 mt-4">
        <Button
          variant="primary"
          onPress={onSave}
          isDisabled={isPending}
          data-testid="practice-plan-save"
        >
          {t('frontend.messages.save')}
        </Button>
        <Button variant="ghost" onPress={onDone}>
          {t('frontend.messages.cancel')}
        </Button>
      </div>
    </Card>
  );
};

type CardActions = {
  onEdit: (plan: PracticePlan) => void;
  onDelete: (plan: PracticePlan) => void;
};

const PracticePlanCard = ({
  plan,
  onEdit,
  onDelete
}: { plan: PracticePlan } & CardActions) => {
  const { t } = useTranslation();

  const pdfUrl = plan.pdf_attachment?.url as string | undefined;
  const thumbUrl = (
    plan.pdf_attachment?.previews as { thumb?: { url?: string } } | undefined
  )?.thumb?.url;

  return (
    <Card className="p-4">
      <div className="flex items-center gap-4">
        <PracticePlanThumb thumbUrl={thumbUrl} />
        <div className="flex-1 min-w-0">
          <Card.Title>{plan.title}</Card.Title>
          {plan.description && (
            <p className="text-sm text-muted">{plan.description}</p>
          )}
          {plan.plan_date && (
            <p className="text-xs text-muted mt-1">
              {parseDate(plan.plan_date)
                .toDate(getLocalTimeZone())
                .toLocaleDateString(getLocale().replaceAll('_', '-'), {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
            </p>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          {pdfUrl && (
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
              <Button
                size="sm"
                variant="ghost"
                isIconOnly
                aria-label={t('frontend.messages.view_practice_plan')}
              >
                <Icon icon="bi:eye" />
              </Button>
            </a>
          )}
          <Button
            size="sm"
            variant="ghost"
            isIconOnly
            aria-label={t('frontend.messages.edit_practice_plan_2')}
            onPress={() => onEdit(plan)}
          >
            <Icon icon="bi:pencil" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            isIconOnly
            aria-label={t('frontend.messages.delete_practice_plan')}
            onPress={() => onDelete(plan)}
            className="text-danger"
          >
            <Icon icon="bi:trash" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

const PastPracticePlans = ({
  teamSeasonId,
  today,
  onEdit,
  onDelete
}: {
  teamSeasonId: number;
  today: string;
  onEdit: (plan: PracticePlan) => void;
  onDelete: (plan: PracticePlan) => void;
}) => {
  const { t } = useTranslation();

  const [isExpanded, setIsExpanded] = useState(false);

  const { results, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useModelIndexInfinite('practice_plan', {
      filter: {
        team_season_id: teamSeasonId,
        plan_date: { lt: today }
      },
      order: ['-plan_date'],
      limit: 20,
      queryOptions: { enabled: isExpanded }
    });
  const plans = results ?? [];

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, isExpanded]);

  return (
    <Disclosure isExpanded={isExpanded} onExpandedChange={setIsExpanded}>
      <Disclosure.Heading>
        <Disclosure.Trigger className="flex items-center gap-2 w-full text-left font-semibold">
          {t('frontend.messages.past_practice_plans')}
          <Disclosure.Indicator />
        </Disclosure.Trigger>
      </Disclosure.Heading>
      <Disclosure.Content>
        <Disclosure.Body>
          {isExpanded && plans.length === 0 && !isFetchingNextPage ? (
            <p className="text-muted py-2">
              {t('frontend.messages.no_past_practice_plans')}
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {plans.map((plan) => (
                <PracticePlanCard
                  key={plan.id}
                  plan={plan}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
              {hasNextPage && (
                <div
                  ref={sentinelRef}
                  className="flex justify-center py-4 text-sm text-muted"
                >
                  {isFetchingNextPage
                    ? t('frontend.messages.loading_more')
                    : ''}
                </div>
              )}
            </div>
          )}
        </Disclosure.Body>
      </Disclosure.Content>
    </Disclosure>
  );
};

const AdminPracticePlans = () => {
  const { t } = useTranslation();

  const { team_season_id } = useParams({ strict: false }) as {
    team_season_id: string;
  };
  const teamSeasonId = Number(team_season_id);
  const today = todayIso();

  const { results: currentResults } = useModelIndex('practice_plan', {
    filter: {
      team_season_id: teamSeasonId,
      plan_date: { gteq: today }
    },
    order: ['plan_date'],
    limit: 100
  });
  const currentPlans = currentResults ?? [];

  const { mutate: deletePlan } = useModelDelete('practice_plan');

  const [mode, setMode] = useState<FormMode>({ kind: 'none' });
  const [deleting, setDeleting] = useState<PracticePlan | null>(null);

  const closeForm = () => setMode({ kind: 'none' });
  const handleEdit = (plan: PracticePlan) =>
    setMode({ kind: 'edit', id: plan.id });

  const handleConfirmDelete = () => {
    if (!deleting) return;
    deletePlan({ id: deleting.id } as never, {
      onSuccess: () => setDeleting(null)
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <Typography.Heading level={1}>
          {t('frontend.messages.practice_plans')}
        </Typography.Heading>
        <Button variant="primary" onPress={() => setMode({ kind: 'create' })}>
          <Icon icon="bi:plus" className="mr-1" />
          {t('frontend.messages.add_plan')}
        </Button>
      </div>

      {mode.kind === 'create' && (
        <ModelCreateSimple
          model="practice_plan"
          parentId={teamSeasonId}
          paths={FIELD_PATHS as never}
          extraDefaultValues={{ plan_date: today }}
          fallback={false}
        >
          <PracticePlanCreateForm onDone={closeForm} />
        </ModelCreateSimple>
      )}

      {mode.kind === 'edit' && (
        <ModelEditSimple
          model="practice_plan"
          modelId={mode.id}
          paths={FIELD_PATHS as never}
          fallback={false}
        >
          <PracticePlanEditForm onDone={closeForm} />
        </ModelEditSimple>
      )}

      <div className="flex flex-col gap-6">
        {currentPlans.length === 0 ? (
          <p className="text-muted">
            {t('frontend.messages.no_upcoming_practice_plans')}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {currentPlans.map((plan) => (
              <PracticePlanCard
                key={plan.id}
                plan={plan}
                onEdit={handleEdit}
                onDelete={setDeleting}
              />
            ))}
          </div>
        )}

        <PastPracticePlans
          teamSeasonId={teamSeasonId}
          today={today}
          onEdit={handleEdit}
          onDelete={setDeleting}
        />
      </div>

      <AlertDialog
        isOpen={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
      >
        <AlertDialog.Backdrop>
          <AlertDialog.Container size="sm">
            <AlertDialog.Dialog>
              <AlertDialog.CloseTrigger />
              <AlertDialog.Header>
                <AlertDialog.Icon status="danger" />
                <AlertDialog.Heading>
                  {t('frontend.messages.delete_practice_plan_2')}
                </AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                {t('frontend.messages.delete_confirmation', {
                  name: deleting?.title as string
                })}
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button variant="ghost" onPress={() => setDeleting(null)}>
                  {t('frontend.messages.cancel')}
                </Button>
                <Button
                  variant="danger"
                  onPress={handleConfirmDelete}
                  data-testid="practice-plan-delete-confirm"
                >
                  {t('frontend.messages.delete')}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </div>
  );
};

export const Route = createFileRoute(
  '/_authenticated/$owner/active/$team_season_id/practice-plans/'
)({
  component: AdminPracticePlans
});
